import { computed, ref, watch } from 'vue';

/**
 * Row-selection state for the records table. Resets whenever `resetOn` changes
 * (e.g. the active subtab) so selections from one subtab never leak into
 * another — Modelos and Rascunhos share id ranges.
 */
export function useRecordSelection(getTotal: () => number, resetOn: () => unknown) {
  const selectedIds = ref<Set<number>>(new Set());

  const selectedCount = computed(() => selectedIds.value.size);
  const hasSelection = computed(() => selectedIds.value.size > 0);
  const allSelected = computed(() => getTotal() > 0 && selectedIds.value.size === getTotal());

  const isSelected = (id: number) => selectedIds.value.has(id);

  const toggle = (id: number) => {
    const next = new Set(selectedIds.value);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    selectedIds.value = next;
  };

  const selectAll = (ids: number[]) => {
    selectedIds.value = allSelected.value ? new Set() : new Set(ids);
  };

  /**
   * Unchecks a single id, for a row that left the listing: a selection the user can no longer see
   * would still be handed to the bulk deletion.
   */
  const remove = (id: number) => {
    if (!selectedIds.value.has(id)) return;

    const next = new Set(selectedIds.value);
    next.delete(id);
    selectedIds.value = next;
  };

  const clear = () => {
    selectedIds.value = new Set();
  };

  watch(resetOn, clear);

  return {
    selectedIds,
    selectedCount,
    hasSelection,
    allSelected,
    isSelected,
    toggle,
    selectAll,
    remove,
    clear,
  };
}
